import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import styled from 'styled-components';

import { PlanDetails } from 'apps/payment/subscription/planDetails';
import { useRouterStore } from 'apps/hooks';
import { isProfileSlug } from 'utils';
import { Div } from 'legacyComponents/styled/lib';
import { Button, ConfirmModal, Tooltip } from 'components';
import { Paragraph } from 'components/Paragraph';

import { isTrialExpired } from 'utils/freeTrials';

import { analytics } from 'core/analytics';

import { ButtonStyled } from '../styles';
import { stripeStore } from '../stripe';

const ReactivePlanButton = styled(ButtonStyled)`
  &:hover {
    background: black;
  }

  opacity: ${props => (props.canReactivePlan ? 1 : 0.5)};
  cursor: ${props => (props.canReactivePlan ? 'pointer' : 'not-allowed')};
`;

export const PlanDescription = withNamespaces()(
  observer(
    ({
      t,
      paymentSpace,
      isLegacySubscription,
      isInitiative,
      trialPeriodEnd,
    }) => {
      const [open, setOpen] = useState(false);
      const routerStore = useRouterStore();
      const { spacePath } = routerStore.route.params;
      const { isEarlyAccess, seatsUsage, subscription, hasProSubscription } =
        paymentSpace;

      const hasCreditCards = !!paymentSpace.creditCards?.length;
      const isInTrial = trialPeriodEnd && !isTrialExpired(trialPeriodEnd);
      const canReactivePlan = isInTrial || hasCreditCards;
      const { isCancelled } = subscription || {};
      const { stripe } = stripeStore;
      const { freeSeats = 0 } = seatsUsage || {};

      const confirmPayment = async card => {
        try {
          if (subscription?.scaSecret) {
            await stripe.confirmCardPayment(subscription.scaSecret, {
              payment_method: card.stripeId,
            });
          }
        } catch (e) {
          analytics.logError(e);
        }
      };

      useEffect(() => {
        const creditCard = paymentSpace.creditCards.find(
          card => card.isDefault,
        );
        if (subscription?.scaSecret && creditCard) {
          confirmPayment(creditCard);
        }
      }, [subscription?.scaSecret]);

      const handleChange = () => {
        const isProfile = isProfileSlug(spacePath);

        if ((!hasProSubscription && isProfile) || isEarlyAccess) {
          routerStore.navigate('home.newInitiative');
          return;
        }

        routerStore.navigate('workspace.upgrade', {
          spacePath,
        });
      };

      if (!subscription) {
        return (
          <Div width='100%' verticalAlign='top' display='inline-block'>
            <div className='text-black w-full flex flex-col my-4'>
              {isEarlyAccess && (
                <div>
                  {t(
                    'This plan has been gifted to early Wikifactory users. All Editors that you invited before the launch of our Subscriptions on 27/09/23, are free forever. ',
                  )}
                </div>
              )}
              {!!freeSeats && (
                <Paragraph>
                  <strong className='pr-2'>{t('Free Seats:')}</strong>
                  <span>{freeSeats}</span>
                </Paragraph>
              )}

              <div className={isEarlyAccess && !!freeSeats ? 'mt-5' : ''}>
                {t(
                  'Ready to make the most out of Wikifactory with your collaborators?',
                )}
                <span
                  className='text-blue-500 cursor-pointer ml-1'
                  onClick={() => {
                    handleChange();
                  }}
                  data-testid='settingBillingPage-upgradePlan-button'
                >
                  Upgrade Now
                </span>
              </div>
            </div>
          </Div>
        );
      }

      return (
        <Div width='100%' display='inline-block' verticalAlign='top'>
          <PlanDetails paymentSpace={paymentSpace} />

          <div className='flex items-center mt-4 h-min'>
            {isInitiative && !isCancelled && (
              <>
                <Button
                  size='small'
                  data-testid={`initiativePage-${
                    !hasProSubscription ? 'upgradeNow' : 'changePlan'
                  }-button`}
                  text={!hasProSubscription ? 'Upgrade Now' : 'Change Plan'}
                  variant='tertiary'
                  iconPosition='right'
                  onClick={handleChange}
                />
                <div className='flex-shrink-0 h-6 mx-2 border-r border-gray-500' />
              </>
            )}

            {isCancelled ? (
              <>
                {!canReactivePlan && (
                  <Tooltip
                    id='reactivePlanDisabled'
                    place='top'
                    delayShow={1000}
                  />
                )}
                <ReactivePlanButton
                  data-testid='settingBillingPage-reactivatePlan-button'
                  text='Reactivate Plan'
                  variant='tertiary'
                  disabled={subscription?.scaSecret}
                  loading={paymentSpace.reactivating}
                  canReactivePlan={canReactivePlan}
                  onClick={() => {
                    if (canReactivePlan) {
                      paymentSpace.ReactivateSubscription();
                    }
                  }}
                  data-tip='Add payment method to reactivate the plan.'
                  data-for='reactivePlanDisabled'
                />
              </>
            ) : (
              <Button
                data-testid='settingBillingPage-cancelSubscription-button'
                style={!isInitiative ? {} : { padding: 0 }}
                className={!isInitiative ? '' : 'hover:text-red-500'}
                text='Cancel Subscription'
                variant={!isInitiative ? 'tertiary' : 'Warning'}
                onClick={() => {
                  setOpen(true);
                }}
              />
            )}

            <ConfirmModal
              open={open}
              loading={paymentSpace.loading}
              title='Are you sure about cancel subscription?'
              onConfirm={() => {
                paymentSpace.CancelSubscription(() => {
                  setOpen(false);
                });
              }}
              onClose={() => {
                setOpen(false);
              }}
            />
          </div>
        </Div>
      );
    },
  ),
);

PlanDescription.propTypes = {
  subscription: PropTypes.object,
};
